<template>
  <el-dialog
    :title="isEdit ? '修改参数' : '新增分析任务'"
    :visible.sync="visible"
    width="1000px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="160px">
      <!-- 基本信息 -->
      <template v-if="!isEdit">
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model="form.taskName" placeholder="请输入任务名称" maxlength="100" />
        </el-form-item>

        <el-form-item label="选择考试批次" prop="planIds">
          <div class="batch-select-wrapper">
            <el-select
              v-model="form.planIds"
              multiple
              placeholder="请选择考试批次（最多5个）"
              :loading="batchLoading"
              class="batch-select"
              style="width: 100%"
              @change="handleBatchChange"
            >
              <el-option
                v-for="item in batchOptions"
                :key="item.planId"
                :label="item.planName"
                :value="item.planId"
              >
                <div class="batch-option">
                  <span :class="['batch-option-checkbox', { checked: isBatchSelected(item.planId) }]"></span>
                  <span class="batch-option-name">{{ item.planName }}</span>
                </div>
              </el-option>
            </el-select>
            <span v-if="form.planIds.length" class="batch-select-summary">已选择 {{ form.planIds.length }} 个批次</span>
          </div>
          <div v-if="form.planIds.length" class="selected-batch-tags">
            <span class="selected-batch-label">已选批次：</span>
            <el-tag
              v-for="planId in form.planIds"
              :key="planId"
              closable
              size="small"
              @close="removeSelectedBatch(planId)"
            >
              {{ getBatchName(planId) }}
            </el-tag>
          </div>
        </el-form-item>
      </template>

      <!-- 编辑模式：任务名称（只读） -->
      <template v-if="isEdit">
        <el-form-item label="任务名称">
          <span>{{ form.taskName }}</span>
        </el-form-item>
      </template>

      <!-- 编辑模式：分析内容（只读：批次名称/课程/试卷/试卷属性） -->
      <template v-if="isEdit && displayCourseList.length > 0">
        <fieldset class="legend-fieldset">
          <legend class="legend-title">科目及试卷配置</legend>
          <el-table :data="displayCourseList" border size="small" style="margin-bottom: 12px;">
            <el-table-column prop="planName" label="批次名称" min-width="180" show-overflow-tooltip />
            <el-table-column prop="courseName" label="课程" min-width="140" show-overflow-tooltip />
            <el-table-column prop="paperName" label="试卷" min-width="200" show-overflow-tooltip>
              <template slot-scope="scope">《{{ scope.row.paperName }}》</template>
            </el-table-column>
            <el-table-column label="试卷属性" width="100" align="center">
              <template slot-scope="scope">{{ String(scope.row.paperForm) === '1' ? '固定卷' : '随机卷' }}</template>
            </el-table-column>
            <el-table-column label="随机卷分析范围" width="300">
              <template slot-scope="scope">
                <template v-if="String(scope.row.paperForm) === '2'">
                  <el-radio-group v-model="scope.row.randomAnalysisAll" size="mini">
                    <el-radio :label="1">全量分析</el-radio>
                    <el-radio :label="0">按抽取次数</el-radio>
                  </el-radio-group>
                  <el-input-number
                    v-if="scope.row.randomAnalysisAll === 0"
                    v-model="scope.row.randomMinExtract"
                    :min="1"
                    size="mini"
                    style="width: 240px; margin-left: 4px"
                  />
                </template>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>
        </fieldset>
      </template>

      <!-- 新建模式：科目及试卷配置（含选择/随机范围编辑） -->
      <template v-if="!isEdit && courseList.length > 0">
        <fieldset class="legend-fieldset">
          <legend class="legend-title">科目及试卷配置</legend>
          <el-table :data="courseList" border size="small">
            <el-table-column prop="planName" label="考试批次" width="150" show-overflow-tooltip />
            <el-table-column prop="courseName" label="科目名称" width="150" />
            <el-table-column label="试卷名称" width="240">
              <template slot-scope="scope">
                《{{ scope.row.paperName }}》 - {{ String(scope.row.paperForm) === '1' ? '固定卷' : '随机卷' }}
              </template>
            </el-table-column>
            <el-table-column label="是否分析" width="80" align="center">
              <template slot-scope="scope">
                <el-checkbox v-model="scope.row.selected" :true-label="1" :false-label="0" />
              </template>
            </el-table-column>
            <el-table-column label="随机卷分析范围" width="300">
              <template slot-scope="scope">
                <template v-if="String(scope.row.paperForm) === '2'">
                  <el-radio-group v-model="scope.row.randomAnalysisAll" size="mini">
                    <el-radio :label="1">全量分析</el-radio>
                    <el-radio :label="0">按抽取次数</el-radio>
                  </el-radio-group>
                  <el-input-number
                    v-if="scope.row.randomAnalysisAll === 0"
                    v-model="scope.row.randomMinExtract"
                    :min="1"
                    size="mini"
                    style="width: 240px; margin-left: 4px"
                  />
                </template>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>
        </fieldset>
      </template>

      <!-- 异常参数设置 -->
      <fieldset class="legend-fieldset">
        <legend class="legend-title">异常题目判定阈值</legend>
        <el-form-item label="正确率异常区间小于" prop="correctRateMin">
          <div style="display: flex; align-items: center; gap: 8px;">
            <el-input-number v-model="form.correctRateMin" :min="0" :max="100" :precision="0" size="small" class="narrow-input-number" />
            <span>%</span>
            <span style="color: #909399; font-size: 12px;">（低于下限为异常）</span>
          </div>
        </el-form-item>
        <el-form-item label="正确率异常区间大于" prop="correctRateMax">
          <div style="display: flex; align-items: center; gap: 8px;">
            <el-input-number v-model="form.correctRateMax" :min="0" :max="100" :precision="0" size="small" class="narrow-input-number" />
            <span>%</span>
            <span style="color: #909399; font-size: 12px;">（高于上限为异常）</span>
          </div>
        </el-form-item>
        <el-form-item label="最小区分度" prop="minDiscrimination">
          <el-input-number v-model="form.minDiscrimination" :min="0" :max="1" :step="0.05" :precision="2" size="small" class="narrow-input-number" />
        </el-form-item>
      </fieldset>

      <!-- 随机卷批量设置（新建/编辑均展示） -->
      <fieldset class="legend-fieldset">
        <legend class="legend-title">随机卷批量设置</legend>
        <el-form-item label="题目分析范围">
          <el-radio-group v-model="form.randomAnalysisAll">
            <el-radio :label="1">全量分析</el-radio>
            <el-radio :label="0">按抽取次数</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.randomAnalysisAll === 0" label="最低抽取次数">
          <el-input-number v-model="form.randomMinExtract" :min="1" size="small" class="narrow-input-number" />
          <span v-if="!isEdit" style="color: #909399; font-size: 12px; margin-left: 8px;">单科目设置的优先于批量设置</span>
        </el-form-item>
      </fieldset>
    </el-form>

    <div slot="footer">
      <el-button @click="visible = false">取 消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">{{ isEdit ? '保 存' : '创 建' }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createTaskAPI, modifyTaskAPI, getBatchOptionsAPI, getCourseOptionsAPI, getTaskDetailAPI } from '@/http/modules/examSet/paperAnalysis'

export default {
  name: 'CreateTaskDialog',
  data() {
    return {
      visible: false,
      isEdit: false,
      editTaskId: '',
      submitting: false,
      batchLoading: false,
      batchOptions: [],
      courseList: [],
      form: {
        taskName: '',
        planIds: [],
        correctRateMin: 20,
        correctRateMax: 95,
        minDiscrimination: 0.15,
        randomMinExtract: 5,
        randomAnalysisAll: 0
      },
      rules: {
        taskName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        planIds: [{ required: true, message: '请选择考试批次', trigger: 'change' }],
        correctRateMin: [{ required: true, message: '请设置正确率异常区间下限', trigger: 'change' }],
        correctRateMax: [{ required: true, message: '请设置正确率异常区间上限', trigger: 'change' }],
        minDiscrimination: [{ required: true, message: '请设置最小区分度', trigger: 'change' }]
      }
    }
  },
  computed: {
    selectedBatchOptions() {
      const selectedPlanIds = this.form.planIds.map(item => String(item))
      return this.batchOptions.filter(item => selectedPlanIds.includes(String(item.planId)))
    },
    // 编辑模式下分析内容只显示已选中的科目；新增模式全部显示
    displayCourseList() {
      if (this.isEdit) {
        return this.courseList.filter(item => item.selected === 1)
      }
      return this.courseList
    }
  },
  methods: {
    open(row) {
      this.visible = true
      if (row && row.taskId) {
        this.isEdit = true
        this.editTaskId = row.taskId
        this.form.taskName = row.taskName || ''
        this.form.correctRateMin = row.correctRateMin
        this.form.correctRateMax = row.correctRateMax
        this.form.minDiscrimination = row.minDiscrimination
        this.form.randomMinExtract = row.randomMinExtract
        this.form.randomAnalysisAll = row.randomAnalysisAll
        this.loadTaskDetail(row.taskId)
      } else {
        this.isEdit = false
        this.loadBatchOptions()
      }
    },
    handleClose() {
      this.form = {
        taskName: '',
        planIds: [],
        correctRateMin: 20,
        correctRateMax: 95,
        minDiscrimination: 0.15,
        randomMinExtract: 5,
        randomAnalysisAll: 0
      }
      this.courseList = []
      this.batchOptions = []
      this.isEdit = false
      this.editTaskId = ''
    },
    async loadTaskDetail(taskId) {
      try {
        const res = await getTaskDetailAPI({ taskId })
        if (res.code === 1 && res.data) {
          // 后端返回结构：{ taskName, correctRateMin, correctRateMax, minDiscrimination,
          //   randomAnalysisAll, randomMinExtract, courseList: [...] }
          if (res.data.taskName) {
            this.form.taskName = res.data.taskName
          }
          if (res.data.correctRateMin !== undefined) this.form.correctRateMin = res.data.correctRateMin
          if (res.data.correctRateMax !== undefined) this.form.correctRateMax = res.data.correctRateMax
          if (res.data.minDiscrimination !== undefined) this.form.minDiscrimination = res.data.minDiscrimination
          if (res.data.randomAnalysisAll !== undefined && res.data.randomAnalysisAll !== null) {
            this.form.randomAnalysisAll = res.data.randomAnalysisAll
          }
          if (res.data.randomMinExtract !== undefined && res.data.randomMinExtract !== null) {
            this.form.randomMinExtract = res.data.randomMinExtract
          }
          this.courseList = (res.data.courseList || []).map(item => ({
            ...item,
            selected: item.selected !== undefined ? item.selected : 1,
            randomAnalysisAll: item.randomAnalysisAll !== undefined && item.randomAnalysisAll !== null ? item.randomAnalysisAll : this.form.randomAnalysisAll,
            randomMinExtract: item.randomMinExtract !== undefined && item.randomMinExtract !== null ? item.randomMinExtract : this.form.randomMinExtract
          }))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadBatchOptions() {
      this.batchLoading = true
      try {
        const res = await getBatchOptionsAPI()
        if (res.code === 1) {
          this.batchOptions = res.data || []
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.batchLoading = false
      }
    },
    isBatchSelected(planId) {
      return this.form.planIds.map(item => String(item)).includes(String(planId))
    },
    getBatchName(planId) {
      const batch = this.batchOptions.find(item => String(item.planId) === String(planId))
      return batch ? batch.planName : planId
    },
    removeSelectedBatch(planId) {
      const planIds = this.form.planIds.filter(item => String(item) !== String(planId))
      this.form.planIds = planIds
      this.handleBatchChange(planIds)
    },
    async handleBatchChange(planIds) {
      if (planIds.length > 5) {
        this.form.planIds = planIds.slice(0, 5)
        this.$message.warning('最多支持5个考试批次')
        return
      }
      if (planIds.length === 0) {
        this.courseList = []
        return
      }
      try {
        const res = await getCourseOptionsAPI({ planIds: planIds.join(',') })
        if (res.code === 1) {
          this.courseList = (res.data || []).map(item => ({
            ...item,
            selected: 1,
            randomAnalysisAll: this.form.randomAnalysisAll,
            randomMinExtract: this.form.randomMinExtract
          }))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
      } catch (e) {
        return
      }

      if (this.form.correctRateMin >= this.form.correctRateMax) {
        this.$message.warning('正确率下限必须小于上限')
        return
      }

      this.submitting = true
      try {
        if (this.isEdit) {
          const courseConfigStringValue = this.courseList
            .filter(item => item.selected === 1 && String(item.paperForm) === '2')
            .map(item => [
              item.id, // 任务课程关联表主键
              item.randomAnalysisAll,
              item.randomAnalysisAll === 0 ? item.randomMinExtract : ''
            ].join('#'))
            .join(',')
          const res = await modifyTaskAPI({
            taskId: this.editTaskId,
            correctRateMin: this.form.correctRateMin,
            correctRateMax: this.form.correctRateMax,
            minDiscrimination: this.form.minDiscrimination,
            randomAnalysisAll: this.form.randomAnalysisAll,
            randomMinExtract: this.form.randomMinExtract,
            courseConfigStringValue
          })
          if (res.code === 1) {
            this.$message.success('修改成功')
            this.visible = false
            this.$emit('success')
          } else {
            this.$message.error(res.msg || '修改失败')
          }
        } else {
          const courseConfigStringValue = this.courseList.map(item => [
            item.planId,
            item.courseId,
            item.paperId,
            item.paperForm,
            item.selected,
            item.randomMinExtract,
            item.randomAnalysisAll
          ].join('#')).join(',')
          const planIdStringValue = (this.form.planIds || []).join(',')
          const res = await createTaskAPI({
            taskName: this.form.taskName,
            planIdStringValue,
            courseConfigStringValue,
            correctRateMin: this.form.correctRateMin,
            correctRateMax: this.form.correctRateMax,
            minDiscrimination: this.form.minDiscrimination,
            randomMinExtract: this.form.randomMinExtract,
            randomAnalysisAll: this.form.randomAnalysisAll
          })
          if (res.code === 1) {
            this.$message.success('创建成功')
            this.visible = false
            this.$emit('success')
          } else {
            this.$message.error(res.msg || '创建失败')
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.narrow-input-number {
  width: 240px;
  /deep/ .el-input__inner {
    padding-left: 32px;
    padding-right: 32px;
    text-align: center;
  }
}
.batch-select-wrapper {
  position: relative;
}
.batch-select {
  /deep/ .el-select__tags {
    display: none;
  }
  /deep/ .el-input__inner {
    padding-left: 120px;
  }
}
.batch-select-summary {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #606266;
  pointer-events: none;
  z-index: 2;
}

.selected-batch-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  line-height: 24px;
}
.selected-batch-label {
  color: #909399;
  font-size: 12px;
}
.batch-option {
  display: flex;
  align-items: center;
  height: 34px;
  line-height: 34px;
}
.batch-option-checkbox {
  position: relative;
  width: 14px;
  height: 14px;
  border: 1px solid #DCDFE6;
  border-radius: 2px;
  box-sizing: border-box;
  background: #fff;
  flex: 0 0 14px;
}
.batch-option-checkbox.checked {
  background: #409EFF;
  border-color: #409EFF;
}
.batch-option-checkbox.checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 1px 1px 0;
  transform: rotate(45deg);
}
.batch-option-name {
  margin-left: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.legend-fieldset {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 16px 16px 4px 16px;
  margin: 12px 0 18px 0;
  .legend-title {
    padding: 0 8px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    width: auto;
    margin-left: 8px;
  }
}
</style>
